/**
 * SEO / GEO 统一配置工具
 * 提供站点默认 meta、结构化数据（JSON-LD）、页面 meta 生成等能力
 */

// ========== 站点基础信息 ==========
export const SITE_CONFIG = {
  name: "光华赋能",
  legalName: "杭州光华赋能教育科技有限公司",
  url: "https://myguanghua.com",
  logo: "https://myguanghua.com/img/logo.2d5c9335.png",
  ogImage: "https://myguanghua.com/img/logo.2d5c9335.png",
  description:
    "光华赋能始于2003年，专注企业科学经营培训与咨询陪跑，提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等服务，用科学经营助推企业高质量发展。",
  keywords:
    "企业培训,管理咨询,科学经营,BLM,EMBA,企业内训,绩效管理,管理微咨询,数字化转型,光华赋能",
  foundingDate: "2003",
  address: "杭州",
  phone: "400-0808-155",
  email: "wangguoli@myguanghua.com"
};

// ========== Organization 结构化数据 ==========
export function getOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_CONFIG.name,
    legalName: SITE_CONFIG.legalName,
    url: SITE_CONFIG.url,
    logo: SITE_CONFIG.logo,
    foundingDate: SITE_CONFIG.foundingDate,
    description: SITE_CONFIG.description,
    telephone: SITE_CONFIG.phone,
    email: SITE_CONFIG.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: SITE_CONFIG.address,
      addressCountry: "CN"
    },
    sameAs: [
      "https://weixin.qq.com/",
      "https://www.weibo.com/"
    ]
  };
}

// ========== WebSite 结构化数据 ==========
export function getWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    description: SITE_CONFIG.description,
    inLanguage: "zh-CN"
  };
}

// ========== BreadcrumbList 结构化数据 ==========
export function getBreadcrumbJsonLd(items: Array<{ name: string; url?: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_CONFIG.url}${item.url}` : undefined
    }))
  };
}

// ========== Service 结构化数据 ==========
export function getServiceJsonLd(options: {
  name: string;
  description: string;
  category?: string;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: options.category || "企业培训与咨询",
    name: options.name,
    description: options.description,
    provider: {
      "@type": "Organization",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url
    },
    areaServed: options.areaServed || "中国"
  };
}

// ========== 生成页面 metaInfo ==========
export interface PageSeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogImage?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  breadcrumb?: Array<{ name: string; url?: string }>;
}

export function generateMetaInfo(options: PageSeoOptions) {
  const fullTitle = options.title;
  const description = options.description;
  const keywords = options.keywords || SITE_CONFIG.keywords;
  const canonical = `${SITE_CONFIG.url}${options.path}`;
  const ogImage = options.ogImage || SITE_CONFIG.ogImage;

  // 收集所有 JSON-LD
  const jsonLdList: Record<string, any>[] = [];
  if (options.breadcrumb && options.breadcrumb.length > 0) {
    jsonLdList.push(getBreadcrumbJsonLd(options.breadcrumb));
  }
  if (options.jsonLd) {
    if (Array.isArray(options.jsonLd)) {
      jsonLdList.push(...options.jsonLd);
    } else {
      jsonLdList.push(options.jsonLd);
    }
  }

  const meta: any[] = [
    { vmid: "description", name: "description", content: description },
    { vmid: "keywords", name: "keywords", content: keywords },
    // Open Graph
    { vmid: "og:title", property: "og:title", content: fullTitle },
    { vmid: "og:description", property: "og:description", content: description },
    { vmid: "og:type", property: "og:type", content: "website" },
    { vmid: "og:url", property: "og:url", content: canonical },
    { vmid: "og:image", property: "og:image", content: ogImage },
    { vmid: "og:site_name", property: "og:site_name", content: SITE_CONFIG.name },
    { vmid: "og:locale", property: "og:locale", content: "zh_CN" },
    // Twitter Card
    { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
    { vmid: "twitter:title", name: "twitter:title", content: fullTitle },
    { vmid: "twitter:description", name: "twitter:description", content: description },
    { vmid: "twitter:image", name: "twitter:image", content: ogImage }
  ];

  const script: any[] = jsonLdList.map((jsonLd, index) => ({
    vmid: `jsonld-${index}`,
    type: "application/ld+json",
    innerHTML: JSON.stringify(jsonLd)
  }));

  return {
    title: fullTitle,
    meta,
    link: [{ rel: "canonical", href: canonical }],
    script,
    __dangerouslyDisableSanitizersByTagID: script.reduce((acc: Record<string, string[]>, _: any, index: number) => {
      acc[`jsonld-${index}`] = ["innerHTML"];
      return acc;
    }, {})
  };
}
